import Icon from '../Utilites/Icon';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { removeDiacritics } from '../Core/Functions';
import { generateQuestionsByAreaAndQuizType } from '../Core/FetchData';
import { createQuestionMediaLoader } from '../Core/QuestionMedia';
import { storageUrl } from '../Core/Config';
import { AnimatePresence } from 'framer-motion';
import { AnimatedRouteQuestion } from '../Utilites/AnimatedRoutes';
import AppLoading from '../Utilites/AppLoading';
import Question from './Question';
import QuizComplete from './QuizComplete';
import SpinnerPlay from './Blocks/SpinnerPlay';
import TextCompletionPlay from './Blocks/TextCompletionPlay';
import ListenPlay from './Blocks/ListenPlay';
import Listen3Play from './Blocks/Listen3Play';
import AudioSelectPlay from './Blocks/AudioSelectPlay';

function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
}

const Play = ({ lang, group, area, quiz, level, onQuit, auth }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [questions, setQuestions] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [answerStatus, setAnswerStatus] = useState(null);
    const [quizCompleted, setQuizCompleted] = useState(false);
    const [score, setScore] = useState(0);
    const [spinnerQuestionIndex, setSpinnerQuestionIndex] = useState(0);
    const [activeSpinnerQuestion, setActiveSpinnerQuestion] = useState(null);
    const [questionsLoading, setQuestionsLoading] = useState(true);
    const loadRequestId = useRef(0);
    const [mediaSession, setMediaSession] = useState(null);

    const resetQuizState = async () => {
        setCurrentQuestionIndex(0);
        setAnswerStatus(null);
        setQuizCompleted(false);
        setScore(0);
        setSpinnerQuestionIndex(0);
        setActiveSpinnerQuestion(null);
        await loadQuestions();
    };

    useEffect(() => {
        if (!area.id || !quiz.id || !lang.code) {
            navigate('/quiz/');
        }
    }, [lang, area, quiz, navigate]);

    useEffect(() => {
        if (!area.id || !quiz.id || !lang.code) {
            return;
        }

        loadQuestions();
        return () => { loadRequestId.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang, area, quiz, level]);

    useEffect(() => {
        if (!mediaSession || quizCompleted || quiz.type === 'spinner') return;
        let active = true;
        const requestId = loadRequestId.current;
        const prepareUpcoming = async () => {
            // Current question first; at most two upcoming questions use the network.
            for (let index = currentQuestionIndex; index <= currentQuestionIndex + 2; index++) {
                const media = await mediaSession.load(index);
                if (!active || requestId !== loadRequestId.current) return;
                setQuestions(previous => {
                    if (!previous[index] || previous[index].mediaReady) return previous;
                    return previous.map((question, i) => i === index ? { ...question, ...media } : question);
                });
            }
        };
        void prepareUpcoming();
        return () => { active = false; };
    }, [mediaSession, currentQuestionIndex, quizCompleted, quiz.type]);

    const loadQuestions = async () => {
        const requestId = loadRequestId.current + 1;
        loadRequestId.current = requestId;
        setQuestionsLoading(true);
        setMediaSession(null);
        setQuestions([]);
        setActiveSpinnerQuestion(null);

        try {
            const questions = await generateQuestionsByAreaAndQuizType(area, quiz, level, { deferImages: true });

            if (requestId !== loadRequestId.current) {
                return;
            }

            if (!Array.isArray(questions)) {
                console.error('generateQuestionsByAreaAndQuizType nevrátil pole:', questions);
                setQuestions([]); // prázdné, aby aplikace nespadla
                return;
            }

            const shuffledQuestions = quiz.type === 'spinner' ? questions : shuffleArray(questions);
            const session = createQuestionMediaLoader(shuffledQuestions, quiz.type);
            // The spinner displays all questions together. Other modes need only the first.
            const initialIndexes = quiz.type === 'spinner'
                ? shuffledQuestions.map((_, index) => index)
                : shuffledQuestions.length ? [0] : [];
            const initialMedia = await Promise.all(initialIndexes.map(index => session.load(index)));
            if (requestId !== loadRequestId.current) return;
            setQuestions(shuffledQuestions.map((question, index) => (
                index < initialMedia.length ? { ...question, ...initialMedia[index] } : question
            )));
            setMediaSession(session);
        } catch (error) {
            if (requestId !== loadRequestId.current) {
                return;
            }

            console.error('Error generating questions:', error);
            setQuestions([]);
        } finally {
            if (requestId === loadRequestId.current) {
                setQuestionsLoading(false);
            }
        }
    };

    const isAnswerCorrect = (answer, question) => {
        if (quiz.type === 'writtenAnswer') {
            const normalize = (str) =>
                removeDiacritics(str)
                    .toLowerCase()
                    .replace(/[^a-zA-Z0-9\s]/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();

            const normalizedAnswer = normalize(answer);
            const normalizedCorrect = normalize(question.correctAnswer);
            return normalizedAnswer === normalizedCorrect;
        }

        if (quiz.type === 'textCompletion') {
            const userWords = answer.split(',').map(w => w.trim().toLowerCase());
            const correctAnswers = question.correctAnswers;

            let correctCount = 0;

            Object.keys(correctAnswers).forEach((gapId, index) => {
                const correctWord = correctAnswers[gapId].toLowerCase();
                const userWord = userWords[index];
                if (userWord === correctWord) {
                    correctCount++;
                }
            });

            return correctCount; // počet správných
        }

        if (quiz.type === 'listen' || quiz.type === 'listen3') {
            // Odpověď je index správné možnosti
            return answer === question.correctIndex;
        }

        return answer === question.correctAnswer;
    };



    const handleAnswer = (answer, answeredQuestion = questions[currentQuestionIndex]) => {
        let isCorrect = isAnswerCorrect(answer, answeredQuestion);
        let delay = 500;
        let audioCount = 0;
        let scoreIncrement = 0;
        let updatedStatus = 'incorrect';

        if (quiz.type === 'audioSelect') {
            const correctSet = new Set((answeredQuestion.correctAnswers || []).map(w => w.toLowerCase()));
            const userArray = Array.isArray(answer) ? answer : (answer ? [answer] : []);
            const userSet = new Set(userArray.map(w => (w || '').toLowerCase()));

            let correctCount = 0;
            let wrongCount = 0;

            userSet.forEach(w => {
                if (correctSet.has(w)) correctCount++;
                else wrongCount++;
            });

            const net = correctCount - wrongCount; // správné mínus špatné
            scoreIncrement = net;
            updatedStatus = `${net}/${answeredQuestion.correctAnswers.length}`;
        } else if (quiz.type === 'textCompletion') {
            const totalGaps = Object.keys(answeredQuestion.correctAnswers || {}).length;
            const correctCount = isCorrect; // isAnswerCorrect vrací počet správných
            scoreIncrement = correctCount;
            updatedStatus = `${correctCount}/${totalGaps}`;
        } else if (quiz.type === 'listen' || quiz.type === 'listen3') {
            if (isCorrect) {
                scoreIncrement = 1;
                updatedStatus = 'correct';
                const successSound = new Audio(storageUrl + '/sounds/question-success.mp3');
                successSound.play();
            } else {
                updatedStatus = 'incorrect';
                const failSound = new Audio(storageUrl + '/sounds/question-fail.mp3');
                failSound.play();
            }
            // Označí odpověď, po 1s přejde na další otázku
            const updatedQuestions = questions.map((q) => {
                if (q.id === answeredQuestion.id) {
                    return {
                        ...q,
                        userAnswer: answer,
                        status: updatedStatus,
                    };
                }
                return q;
            });
            setQuestions(updatedQuestions);
            setAnswerStatus(updatedStatus);
            setScore((prev) => prev + scoreIncrement);
            setTimeout(() => {
                if (currentQuestionIndex < questions.length - 1) {
                    setCurrentQuestionIndex(currentQuestionIndex + 1);
                    setAnswerStatus(null);
                } else {
                    setQuizCompleted(true);
                }
            }, 1000);
            return;
        } else {
            if (isCorrect) {
                scoreIncrement = 1;
                updatedStatus = 'correct';

                if (quiz.type === 'yesNo') {
                    const successSound = new Audio(storageUrl + '/sounds/question-success.mp3');
                    successSound.play();
                }

                audioCount = [answeredQuestion.audio1, answeredQuestion.audio2, answeredQuestion.audio3]
                    .filter(src => !!src).length;

                delay = audioCount === 3 ? 6000 : audioCount === 1 ? 2000 : delay;
            } else {
                const failSound = new Audio(storageUrl + '/sounds/question-fail.mp3');
                failSound.play();
            }
        }

        if (quiz.type === 'spinner') {
            setActiveSpinnerQuestion(answeredQuestion);
        }

        const updatedQuestions = questions.map((q) => {
            if (q.id === answeredQuestion.id) {
                return {
                    ...q,
                    userAnswer: answer,
                    status: updatedStatus,
                };
            }
            return q;
        });

        setQuestions(updatedQuestions);
        setAnswerStatus(updatedStatus);
        setScore((prev) => prev + scoreIncrement);

        setTimeout(() => {
            if (quiz.type === 'spinner') {
                if (updatedQuestions.some(q => q.status == null)) {
                    setAnswerStatus(null);
                    setSpinnerQuestionIndex(prev => prev + 1);
                } else {
                    setQuizCompleted(true);
                }
            } else {
                if (currentQuestionIndex < questions.length - 1) {
                    setCurrentQuestionIndex(currentQuestionIndex + 1);
                    setAnswerStatus(null);
                } else {
                    setQuizCompleted(true);
                }
            }
        }, delay);
    };


    const currentQuestion = questions?.length > 0 ? questions[currentQuestionIndex] : null;

    if (questionsLoading || (currentQuestion && !currentQuestion.mediaReady)) {
        return <AppLoading>Načítám otázky...</AppLoading>;
    }

    if (quizCompleted) {
        return (
            <div id="quiz-play">
                <QuizComplete score={score} questions={questions} resetQuizState={resetQuizState} quiz={quiz} lang={lang} group={group} area={area} auth={auth} />
            </div>
        );
    }

    if (quiz.type === 'audioSelect' && questions.length > 0) {
        return (
            <div id="quiz-play">
                <nav id="quiz-nav">
                    <h3 className="text-primary">
                        {t('question')} {currentQuestionIndex + 1} {t('of')} {questions.length}
                    </h3>
                </nav>
                <AudioSelectPlay
                    question={questions[currentQuestionIndex]}
                    handleAnswer={handleAnswer}
                    answerStatus={answerStatus}
                />
            </div>
        );
    }

    if (quiz.type === 'spinner' && questions.length > 0) {
        return (
            <div id="quiz-play">
                <nav id="quiz-nav">
                    <h3 className="text-primary">
                        {t('question')} {spinnerQuestionIndex + 1} {t('of')} {questions.length}
                    </h3>
                </nav>
                <div className="spinner-question-wrapper">
                    <SpinnerPlay
                        questions={questions}
                        handleAnswer={handleAnswer}
                        onComplete={() => setQuizCompleted(true)}
                        setCurrentSpinnerQuestion={setActiveSpinnerQuestion}
                    />

                    {/* Zobrazíme otázku a přehrávání, pokud je vybraná */}
                    {activeSpinnerQuestion && (
                        <Question
                            question={activeSpinnerQuestion}
                            questions={questions}
                            quiz={quiz}
                            area={area}
                            handleAnswer={handleAnswer}
                            answerStatus={answerStatus}
                            onComplete={() => setQuizCompleted(true)}
                        />
                    )}
                </div>
            </div>
        );
    }


    if (quiz.type === 'listen3' && questions.length > 0) {
        return (
            <div id="quiz-play">
                <nav id="quiz-nav">
                    <h3 className="text-primary">
                        {t('question')} {currentQuestionIndex + 1} {t('of')} {questions.length}
                    </h3>
                </nav>
                <Listen3Play
                    question={questions[currentQuestionIndex]}
                    handleAnswer={(idx) => {
                        handleAnswer(idx, questions[currentQuestionIndex]);
                    }}
                    answerStatus={answerStatus}
                    onNext={() => {
                        if (currentQuestionIndex < questions.length - 1) {
                            setCurrentQuestionIndex(currentQuestionIndex + 1);
                            setAnswerStatus(null);
                        } else {
                            setQuizCompleted(true);
                        }
                    }}
                />
            </div>
        );
    }

    if (quiz.type === 'listen' && questions.length > 0) {
        return (
            <div id="quiz-play">
                <nav id="quiz-nav">
                    <h3 className="text-primary">
                        {t('question')} {currentQuestionIndex + 1} {t('of')} {questions.length}
                    </h3>
                </nav>
                <ListenPlay
                    question={questions[currentQuestionIndex]}
                    handleAnswer={(idx) => {
                        handleAnswer(idx, questions[currentQuestionIndex]);
                    }}
                    answerStatus={answerStatus}
                    onNext={() => {
                        if (currentQuestionIndex < questions.length - 1) {
                            setCurrentQuestionIndex(currentQuestionIndex + 1);
                            setAnswerStatus(null);
                        } else {
                            setQuizCompleted(true);
                        }
                    }}
                />
            </div>
        );
    }

    if (quiz.type === 'textCompletion' && questions.length > 0) {
        return (
            <div id="quiz-play">
                <TextCompletionPlay
                    key={questions[currentQuestionIndex].id}
                    question={questions[currentQuestionIndex]}
                    handleAnswer={handleAnswer}
                    answerStatus={answerStatus}
                />
            </div>
        );
    }


    if (currentQuestion) {
        return (
            <div id="quiz-play">
                <nav id="quiz-nav">
                    <h3 className="text-primary">
                        {t('question')} {currentQuestionIndex + 1} {t('of')} {questions.length}
                    </h3>
                </nav>

                <AnimatePresence mode="wait">
                    <AnimatedRouteQuestion key={`question-${currentQuestion.id}`}>
                        <Question
                            question={currentQuestion}
                            questions={questions}
                            quiz={quiz}
                            area={area}
                            handleAnswer={handleAnswer}
                            answerStatus={answerStatus}
                        />
                    </AnimatedRouteQuestion>
                </AnimatePresence>
            </div>
        );
    }

    return (
        <div id="quiz-question">
            <h3 className="text-red">{t('no-questions-in-this-quiz')}</h3>
            <button onClick={onQuit} className="btn btn-primary">
                <Icon name="arrow_back" /> {t('back-to-quiz-selection')}
            </button>
        </div>
    );
};

export default Play;
